/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import useSWR from 'swr';
import qs from 'qs';

import type * as Type from '@/common/interface';
import request from '@/utils/request';

export const useCreationCoinSummary = () => {
  const apiUrl = '/answer/api/v1/creation-coins/summary';
  const { data, error, mutate } = useSWR<Type.CreationCoinSummary, Error>(
    apiUrl,
    request.instance.get,
  );
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const useCreationCoinLedgers = (params: Type.Paging) => {
  const apiUrl = `/answer/api/v1/creation-coins/ledger?${qs.stringify(params, {
    skipNulls: true,
  })}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.CreationCoinLedgerItem>,
    Error
  >(apiUrl, request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const tipCreationCoin = (params: Type.CreationCoinTipReq) => {
  return request.post<Type.CreationCoinTipResp>(
    '/answer/api/v1/creation-coins/tip',
    params,
  );
};

export const useCreationCoinQuestionBounty = (questionId?: string) => {
  const apiUrl = questionId
    ? `/answer/api/v1/creation-coins/bounty/question?${qs.stringify({
        question_id: questionId,
      })}`
    : null;
  const { data, error, mutate } = useSWR<
    Type.CreationCoinQuestionBounty,
    Error
  >(apiUrl, request.instance.get);
  return {
    data,
    isLoading: Boolean(questionId) && !data && !error,
    error,
    mutate,
  };
};

export const createCreationCoinBounty = (
  params: Type.CreationCoinBountyCreateReq,
) => {
  return request.post<Type.CreationCoinQuestionBounty>(
    '/answer/api/v1/creation-coins/bounty',
    params,
  );
};
